import type { ProjectFolder, ProjectWithRole } from '../types';

export interface FolderGroup {
  folder: ProjectFolder;
  projects: ProjectWithRole[];
}

export interface GroupedProjects {
  /** Pastas com pelo menos um projeto visivel, na ordem em que vieram. */
  groups: FolderGroup[];
  /** O que nao esta em pasta nenhuma que eu enxergue. */
  unfiled: ProjectWithRole[];
  /** Existe alguma pasta neste escopo? Decide se o rotulo "Sem pasta" aparece. */
  hasFolders: boolean;
}

/**
 * Agrupa uma fatia da lista de projetos pelas pastas de um escopo.
 *
 * Fora do componente de proposito: e a regra que decide se um projeto aparece
 * duas vezes, uma vez ou nenhuma na sidebar, e isso merece teste sem render.
 *
 * Duas decisoes que valem mais que o codigo:
 *
 *   1. Pasta que eu nao enxergo nao esconde projeto. Um projeto pode carregar
 *      `folder_id` de uma pasta pessoal de outra pessoa (ela e admin do projeto
 *      e o guardou na gaveta dela). Como o RLS nao me devolve essa pasta, o
 *      projeto cai em "sem pasta" em vez de sumir da minha lista.
 *   2. Pasta vazia continua aparecendo, mas so via `folders`; `groups` traz
 *      apenas as que tem projeto visivel. Quem renderiza decide se mostra as
 *      vazias (a sidebar mostra, para a pasta recem-criada nao piscar e sumir).
 */
export function groupProjectsByFolder(
  projects: ProjectWithRole[],
  folders: ProjectFolder[]
): GroupedProjects {
  const byFolder = new Map<string, ProjectWithRole[]>();
  for (const folder of folders) {
    byFolder.set(folder.id, []);
  }

  const unfiled: ProjectWithRole[] = [];
  for (const project of projects) {
    const bucket = project.folder_id ? byFolder.get(project.folder_id) : undefined;
    if (bucket) {
      bucket.push(project);
    } else {
      unfiled.push(project);
    }
  }

  return {
    groups: folders
      .map(folder => ({ folder, projects: byFolder.get(folder.id) ?? [] }))
      .filter(group => group.projects.length > 0),
    unfiled,
    hasFolders: folders.length > 0,
  };
}

/**
 * As pastas de um escopo. `organizationId` nulo devolve as pastas pessoais do
 * usuario; preenchido, as daquela organizacao.
 *
 * `ownerId` importa so no escopo pessoal: o RLS ja garante que eu nao recebo
 * pasta pessoal de terceiro, mas filtrar aqui tambem deixa a regra visivel no
 * lugar onde ela e lida.
 */
export function foldersForScope(
  folders: ProjectFolder[],
  organizationId: string | null,
  ownerId: string | null | undefined
): ProjectFolder[] {
  if (organizationId) {
    return folders.filter(f => f.organization_id === organizationId);
  }
  return folders.filter(f => f.organization_id === null && f.owner_id === ownerId);
}
